/**
 * enemySpriteData.ts — Sprite sheet descriptors and animation clip maps
 * for all enemy types (Nibbler, Thornhop, GloomBat).
 *
 * Extracted from SpriteRenderer.ts to keep both files under the 300-line limit.
 * Pure data — no draw calls, no game state.
 *
 * @module enemySpriteData
 */

import type { SpriteSheet, AnimClip } from './SpriteRenderer';

// ── Nibbler ───────────────────────────────────────────────────────────────

/**
 * Nibbler sprite sheet (v3 - Client Draft): 3600 × 300 px, 9 frames at 400 × 300 px.
 * Rebuilt from raw draft pieces, horizontally centered and bottom aligned
 * Frame map (0-indexed):
 *   0: Patrol A, 1: Patrol B, 2: Turn A, 3: Turn B / Anticipate,
 *   4: Extra, 5: White Placeholder, 6: Hurt Flinch, 7: Small Ball, 8: Dead Explosion
 */
export const NIBBLER_SHEET: SpriteSheet = {
  sheetWidth: 3600,
  sheetHeight: 300,
  frameWidth: 400,
  frameHeight: 300,
  frameCount: 9,
};

export type NibblerAnimState = 'IDLE' | 'PATROL' | 'ALERT' | 'ATTACK' | 'HURT' | 'DEAD' | 'RECOVER';

export const NIBBLER_CLIPS: Record<NibblerAnimState, AnimClip> = {
  IDLE:    { start: 0, end: 1, fps: 3,  loop: true  },
  PATROL:  { start: 0, end: 1, fps: 6,  loop: true  },
  ALERT:   { start: 2, end: 2, fps: 1,  loop: false },
  ATTACK:  { start: 3, end: 4, fps: 10, loop: false },
  HURT:    { start: 6, end: 6, fps: 12, loop: false },
  DEAD:    { start: 7, end: 8, fps: 4,  loop: false },
  RECOVER: { start: 7, end: 7, fps: 1,  loop: false },
};

// ── Thornhop ──────────────────────────────────────────────────────────────

/**
 * Thornhop sprite sheet (v5 - cleaned): 3200 × 400 px, 8 frames at 400 × 400 px.
 * Ghost pixels stripped (alpha < 80), 87px+ padding per side. Sprites face RIGHT.
 * Frame map (0-indexed):
 *   0: Idle A, 1: Idle B/Bob, 2: Crouch/Charge, 3: Takeoff Burst,
 *   4: Airborne Rise, 5: Airborne Fall, 6: Land/Settle, 7: Defeated
 */
export const THORNHOP_SHEET: SpriteSheet = {
  sheetWidth: 3200,
  sheetHeight: 400,
  frameWidth: 400,
  frameHeight: 400,
  frameCount: 8,
};

export type ThornhopAnimState = 'IDLE' | 'CROUCH' | 'TAKEOFF' | 'RISE' | 'FALL' | 'LAND' | 'DEAD';

export const THORNHOP_CLIPS: Record<ThornhopAnimState, AnimClip> = {
  IDLE:    { start: 0, end: 1, fps: 4,  loop: true  },
  CROUCH:  { start: 2, end: 2, fps: 8,  loop: false },
  TAKEOFF: { start: 3, end: 3, fps: 12, loop: false },
  RISE:    { start: 4, end: 4, fps: 10, loop: false },
  // DECISION: Frame 5 is a landing-impact frame with crack effects, not a
  // clean airborne pose. Reuse frame 4 (RISE) for FALL so the Thornhop
  // keeps a consistent aerial silhouette throughout the jump arc.
  FALL:    { start: 4, end: 4, fps: 8,  loop: true  },
  LAND:    { start: 6, end: 6, fps: 10, loop: false },
  DEAD:    { start: 7, end: 7, fps: 1,  loop: false },
};

// ── GloomBat ──────────────────────────────────────────────────────────────

/**
 * GloomBat sprite sheet (v3 - Client Draft): 4000 × 400 px, 10 frames at 400 × 400 px.
 * Frame map (0-indexed):
 *   0-3: Patrol (A, B, C, B ping-pong)
 *   4-5: Turn, 6: Hurt Flash, 7: Hurt Flinch, 8: Death poof, 9: Dead dissolve
 */
export const GLOOM_BAT_SHEET: SpriteSheet = {
  sheetWidth: 4000,
  sheetHeight: 400,
  frameWidth: 400,
  frameHeight: 400,
  frameCount: 10,
};

export type GloomBatAnimState = 'FLY' | 'GLIDE' | 'DIVE' | 'AGGRO' | 'HURT' | 'DEAD';

// Frames generated via Centered Extraction
export const GLOOM_BAT_CLIPS: Record<GloomBatAnimState, AnimClip> = {
  FLY:   { start: 0, end: 3, fps: 16, loop: true  },
  GLIDE: { start: 2, end: 2, fps: 4, loop: true  },
  AGGRO: { start: 4, end: 5, fps: 12, loop: true  },
  DIVE:  { start: 4, end: 5, fps: 12, loop: false },
  HURT:  { start: 6, end: 7, fps: 8, loop: false },
  DEAD:  { start: 8, end: 9, fps: 4, loop: false },
};
